import { useCallback } from 'react';

import { ViewType } from '@this+that/models';
import log from 'loglevel';

import { APP_PAGES } from '../../routes/constants';
import { useFetchCacheStore } from '../../store/FetchCache';
import { INBOX_FEATURES_LIMIT } from '../../utils/constants';
import { useGetSlackIntegrations } from '../Integrations';
import { useFetchLabels } from '../Labels';
import { useFetchTaskGroups } from '../taskGroups';
import {
  useFetchIndividualUserCommunications,
  useInboxFetchGroups,
} from '../UserCommunications';

// Shared module-wide (not per-hook-instance) so concurrent callers from different hook instances join one fetch instead of duplicating it.
const inFlightViewTypeFetches = new Map<string, Promise<void>>();

const buildInFlightKey = (
  viewType: ViewType,
  emailAddresses?: string[],
): string => `${viewType}|${JSON.stringify(emailAddresses ?? null)}`;

export const clearInFlightViewTypeFetches = () => {
  inFlightViewTypeFetches.clear();
};

export const useSmartDataFetch = () => {
  const { isViewTypeLoaded, markViewTypeAsLoaded, isSlackChannelsLoaded } =
    useFetchCacheStore();

  const { handleFetchIndividualUserCommunications } =
    useFetchIndividualUserCommunications({
      limit: INBOX_FEATURES_LIMIT,
      isInitialFetch: true,
    });

  const { combinedViewTypes } = useInboxFetchGroups();

  const { handleFetchLabels } = useFetchLabels();
  const { handleFetchSlackIntegrations } = useGetSlackIntegrations();
  const { handleFetchTaskGroups } = useFetchTaskGroups();

  const runViewTypeFetch = useCallback(
    async (
      viewType: ViewType,
      isCombined: boolean,
      emailAddresses?: string[],
    ) => {
      try {
        if (isCombined) {
          await handleFetchIndividualUserCommunications(
            combinedViewTypes,
            false,
            emailAddresses,
          );
          combinedViewTypes.forEach((vt) => markViewTypeAsLoaded(vt));
        } else {
          await handleFetchIndividualUserCommunications(
            viewType,
            false,
            emailAddresses,
          );
          markViewTypeAsLoaded(viewType);
        }
      } catch (error) {
        log.error(`Error fetching user communications for ${viewType}:`, error);
      }
    },
    [
      handleFetchIndividualUserCommunications,
      combinedViewTypes,
      markViewTypeAsLoaded,
    ],
  );

  const fetchViewType = useCallback(
    async (viewType: ViewType, emailAddresses?: string[], force = false) => {
      const hasFilters =
        emailAddresses !== undefined && emailAddresses.length > 0;
      const shouldSkipFetch =
        !force && !hasFilters && isViewTypeLoaded(viewType);

      if (shouldSkipFetch) {
        log.info(`ViewType ${viewType} already loaded, skipping fetch`);
        return;
      }

      const isCombined = combinedViewTypes.includes(viewType);
      const key = buildInFlightKey(viewType, emailAddresses);
      const existingFetch = inFlightViewTypeFetches.get(key);

      if (existingFetch) {
        return existingFetch;
      }

      const keysToRegister = isCombined
        ? combinedViewTypes.map((vt) => buildInFlightKey(vt, emailAddresses))
        : [key];

      const fetchPromise = runViewTypeFetch(
        viewType,
        isCombined,
        emailAddresses,
      ).finally(() => {
        keysToRegister.forEach((k) => inFlightViewTypeFetches.delete(k));
      });

      keysToRegister.forEach((k) =>
        inFlightViewTypeFetches.set(k, fetchPromise),
      );

      return fetchPromise;
    },
    [isViewTypeLoaded, combinedViewTypes, runViewTypeFetch],
  );

  const fetchCommonData = useCallback(
    async (currentPath?: string) => {
      const isDoBoxRoute = currentPath?.startsWith(APP_PAGES.DOBOX) ?? false;

      try {
        const tasks: Promise<unknown>[] = [
          handleFetchLabels(),
          handleFetchSlackIntegrations(),
        ];

        if (isDoBoxRoute) {
          tasks.push(handleFetchTaskGroups());
        }

        await Promise.all(tasks);
      } catch (error) {
        log.error('Error fetching common data:', error);
      }
    },
    [handleFetchLabels, handleFetchSlackIntegrations, handleFetchTaskGroups],
  );

  const fetchInitialData = useCallback(
    async (currentPath?: string) => {
      await fetchCommonData(currentPath);
    },
    [fetchCommonData],
  );

  return {
    fetchViewType,
    fetchCommonData,
    fetchInitialData,
    isViewTypeLoaded,
    isSlackChannelsLoaded,
  };
};
